import { Injectable, Injector } from '@angular/core';
import { ConfigProvider } from "../../config-provider.service";
import { UtilsProvider } from "./utils-provider.service";
import { ApiBaseService } from "./api-base-service";
import { EMPTY, forkJoin, from, Observable, of, switchMap, tap, throwError } from "rxjs";
import { catchError, finalize, map, shareReplay } from "rxjs/operators";
import { KeyValueStorage } from "./key-value-storage";
import { User } from "../models/user";
import { Organization } from "../models/organization";
import { OrganizationsProvider } from "../../organizations/providers/organizations-provider";
import { OrganizationMembership_OrganizationRole } from "../models/organization-membership_organization-role";
import { OrganizationRole } from "../models/organization-role";
import { CoachesProvider } from "../../coaches/providers/coaches-provider";
import { AthletesProvider } from "../../athletes/providers/athletes-provider";
import { GuestsProvider } from "../../guests/providers/guests-provider";
import { UsersProvider } from "../../users/providers/users-provider";
import { OrganizationMembership } from "../models/organization-membership";

@Injectable({
  providedIn: 'root'
})
export class CurrentUser {
  // caching the current user
  private _currentUser$: Observable<User> | null = null;

  public user: User | null = null;
  public organization: Organization | null = null;

  private accessToken: string | null = null;
  private loginRequest?: Observable<boolean>;
  private hasPendingRequest = false;

  private _organizationProvider: OrganizationsProvider | null = null;
  private _coachesProvider: CoachesProvider | null = null
  private _athletesProvider: AthletesProvider | null = null;
  private _guestsProvider: GuestsProvider | null = null;
  private get organizationProvider(): OrganizationsProvider {
    if (!this._organizationProvider) {
      this._organizationProvider = this.injector.get(OrganizationsProvider);
    }
    return this._organizationProvider;
  }
  private get coachesProvider(): CoachesProvider {
    if (!this._coachesProvider) {
      this._coachesProvider = this.injector.get(CoachesProvider);
    }
    return this._coachesProvider;
  }
  private get athletesProvider(): AthletesProvider {
    if (!this._athletesProvider) {
      this._athletesProvider = this.injector.get(AthletesProvider);
    }
    return this._athletesProvider;
  }
  private get guestsProvider(): GuestsProvider {
    if (!this._guestsProvider) {
      this._guestsProvider = this.injector.get(GuestsProvider);
    }
    return this._guestsProvider;
  }


  constructor(
    private injector: Injector,
    private configProvider: ConfigProvider,
    private utilsProvider: UtilsProvider,
    private api: ApiBaseService,
    private _keyValueStore: KeyValueStorage,
  ) {
  }

  public invalidateCurrentUserCache(): void {
    this._currentUser$ = null;
  }

  public checkEmail(email: string): Observable<any> {
    this._currentUser$ = null;
    const params: Object = {email};
    return this.api.call('get', 'auth/check-email', params).pipe(
        map((result) => result),
        catchError((error) => throwError(() => error))
    );
  }

  public getCurrentUser(): Observable<User> {
    if (!this._currentUser$) {
      this._currentUser$ = this.api.call('get', 'user/', {}, this.getAccessToken()).pipe(
        map((result: User) => User.fromAPI(result)),
        shareReplay(1), // Cache la dernière émission pour tous les abonnés
        catchError((error) => {
          // En cas d'erreur, on réinitialise le cache
          this.invalidateCurrentUserCache();
          return throwError(() => error);
        })
      );
    }
    return this._currentUser$;
  }

  public checkResetToken(email: string, token: string): Observable<any> {
    const params: Object = {email, token};
    return this.api.call('get', 'check-reset-token', params).pipe(
      map((result) => result),
      catchError((error) => throwError(() => error))
    );
  }

  public getOrganizationsMemberships(): Observable<OrganizationMembership[]> {
    if (!this.user?.id) {
      return throwError(() => ({code: 4, message: 'No user'}));
    }
    return this.getCurrentUser().pipe(
      map((result: User) => {
        return OrganizationMembership.fromAPIList(result.organizationMemberships);
      }),
      catchError((error) => throwError(() => error))
    );
  }

  public getMainHomeUrl(organization: Organization, roles: number[], defaultUrl = '/organizations'): Observable<string> {
    if (!this.user?.id) throw new Error('No user');
    if (!organization.id) throw new Error('No organization');
    return this.getMainHomeUrlFromRoles(organization, roles, defaultUrl);
  }

  private getMainHomeUrlFromRoles(organization: Organization, roles: number[], defaultUrl: string): Observable<string> {
    if (!this.user?.id) throw new Error('No user');
    if (!organization.id) throw new Error('No organization');
    if (!roles) throw new Error('No roles');

    return this.getCurrentUser().pipe(
      map((user: User) => {
        if (roles.indexOf(OrganizationRole.ID_ADMIN) > -1) {
          return defaultUrl;
        } else if (roles.indexOf(OrganizationRole.ID_SUPERCOACH) > -1) {
          return '/' + organization.code + '/super-coaches/' + user.id;
        } else if (roles.indexOf(OrganizationRole.ID_COACH) > -1) {
          return '/' + organization.code + '/coaches/' + user.id;
        } else if (roles.indexOf(OrganizationRole.ID_ATHLETE) > -1) {
          return '/' + organization.code + '/app/athletes/' + user.id;
        } else if (roles.indexOf(OrganizationRole.ID_GUEST) > -1) {
          // le dashboard n'est pas implémenté pour le moment là
          return '/' + organization.code + '/athletes';
        } else {
          return defaultUrl;
        }
      }),
    );
  }

  public login(email: string, password: string, remember: boolean, eulaAccepted: boolean): Observable<boolean> {
    this._currentUser$ = null;
    if (!this.hasPendingRequest) {
      this.hasPendingRequest = true;
      this.loginRequest = of(null).pipe(
        switchMap((): Observable<boolean> => {
          if (this.isLoggedIn()) {
            this.hasPendingRequest = false;
            return of(true);
          } else {
            this.hasPendingRequest = true;
            return this.api.call('post', 'token', {
              grant_type: 'password',
              client_id: this.configProvider.apiPasswordGrantClient,
              client_secret: this.configProvider.apiPasswordGrantSecret,
              username: email,
              password,
              scope: '*',
              eula_accepted: eulaAccepted
            }).pipe(
              switchMap(result => {
                this.accessToken = result.access_token;
                return this.retrieveUser(result.access_token).pipe(
                  tap(() => {
                    this.saveAccessToken(remember);
                    // this.organization = null;
                  }),
                  catchError(error => {
                    this.hasPendingRequest = false;
                    console.error('Error logging in', error)
                    return throwError(() => ({code: 2, message: 'Could not retrieve user data', originalError: error}));
                  })
                );
              }),
              catchError(error => {
                this.hasPendingRequest = false;
                switch (error.error.message) {
                  case 'Unauthorized':
                    return throwError(() => ({code: 1, message: 'Wrong email/password', originalError: error}));
                  case 'Account disabled':
                    return throwError(() => ({code: 3, message: 'Account disabled', originalError: error}));
                  default:
                    return throwError(() => ({code: 2, message: 'Could not retrieve user data', originalError: error}));
                }

              }),
              tap(() => this.hasPendingRequest = false)
            );
          }
        }),
        catchError(error => {
          // Handle or log the error appropriately
          this.hasPendingRequest = false;
          console.error('Error logging in', error)
          return throwError(() => error);
        })
      );
    }

    return this.loginRequest || of(false);
  }

  // public tryLoginFromRemember(): Observable<boolean> {
  //   // If there's already a pending request, return an observable that emits a specific value.
  //   // Depending on your logic, you might want to emit `false`, `true`, or even use `EMPTY` to not emit anything.
  //   // For this example, let's assume you want to return `false` to indicate that no new login attempt was made.
  //   if (this.hasPendingRequest) {
  //     console.info('A login request is already pending.');
  //     return of(false);
  //   }
  //
  //   const loginObservable = new Observable<boolean>((observer) => {
  //     this.hasPendingRequest = true;
  //
  //     if (this.isLoggedIn()) {
  //       console.info('Already logged in.');
  //
  //       this.hasPendingRequest = false;
  //       observer.next(true);
  //       observer.complete();
  //     } else if (this.retrieveAccessToken()) {
  //       console.info( 'Access token retrieved from storage.');
  //
  //       this.retrieveUser(this.accessToken).subscribe({
  //         next: () => {
  //           console.info('User data retrieved.');
  //           this.hasPendingRequest = false;
  //           observer.next(true);
  //           observer.complete();
  //         },
  //         error: () => {
  //           console.error('Could not retrieve user data.');
  //           this.hasPendingRequest = false;
  //           observer.next(false);
  //           observer.complete();
  //         }
  //       });
  //     } else {
  //       console.info('No access token found.');
  //       this.hasPendingRequest = false;
  //       observer.next(false);
  //       observer.complete();
  //     }
  //   });
  //   console.info('Returning login request observable.');
  //   this.loginRequest = loginObservable;
  //   console.info('loginRequest:', this.loginRequest);
  //   return loginObservable;
  // }
  public tryLoginFromRemember(): Observable<boolean> {

    this.hasPendingRequest = true;

    return new Observable<boolean>((observer) => {
      // Check if already logged in
      if (this.isLoggedIn()) {
        observer.next(true);
        observer.complete();
      } else {
        // Attempt to retrieve the access token
        const token = this.retrieveAccessToken();
        if (token) {
          this.retrieveUser(this.accessToken).subscribe({
            next: () => {
              observer.next(true);
              observer.complete();
            },
            error: (err) => {
              console.log("Error retrieving user data", err);
              observer.next(false);
              observer.complete();
            }
          });
        } else {
          observer.next(false);
          observer.complete();
        }
      }
    }).pipe(
      finalize(() => this.hasPendingRequest = false)  // Ensure hasPendingRequest is reset when the observable completes or errors.
    );
  }

  public logout(): Observable<void> {
    return new Observable((observer) => {

      this.user = null;
      this.accessToken = '';
      this._keyValueStore.clear(KeyValueStorage.STORE_MEMORY);
      this._keyValueStore.clear(KeyValueStorage.STORE_SESSION);
      this._keyValueStore.clear(KeyValueStorage.STORE_LOCAL);

      observer.next();
      observer.complete();
    });
  }

  public isLoggedIn(): boolean {
    return this.user != null && this.accessToken != null;
  }

  public getAccessToken() {
    return this.accessToken;
  }

  public sendPasswordResetEmail(email: string, resetUrl: string): Observable<any> {
    return this.api.call('post', 'reset-password-token', {
      email,
      resetUrl
    });
  }

  public resetPassword(email: string, password: string, token: string): Observable<any> {
    return this.api.call('post', 'reset-password', {
      token,
      email,
      password,
      password_confirmation: password
    });
  }

  public register(email: string, password: string, remember: boolean, langId: number, firstName: string, lastName: string, eulaAccepted: boolean): Observable<any> {
    return this.api.call('post', 'auth/register', {
      email,
      password,
      firstName,
      lastName,
      langId
    }).pipe(
      switchMap((result) => {
        return this.login(email, password, remember, eulaAccepted).pipe(
          map(() => result)
        );
      })
    );
  }

  private saveAccessToken(remember = true): void {
    this._keyValueStore.set(
        'currentUser_accessToken',
        this.accessToken,
        remember ? KeyValueStorage.STORE_LOCAL : KeyValueStorage.STORE_SESSION
    );
  }

  public retrieveAccessToken(): boolean {
    let accessToken = this._keyValueStore.get('currentUser_accessToken', null, KeyValueStorage.STORE_LOCAL);
    if (!accessToken) {
        accessToken = this._keyValueStore.get('currentUser_accessToken', null, KeyValueStorage.STORE_SESSION);
    }

    if (accessToken) {
        this.accessToken = accessToken;
        return true;
    }
    return false;
  }

  private retrieveUser(accessToken: string | null): Observable<any> {
    if (!accessToken) {
      console.error('No access token provided.')
      return throwError(() => new Error('No access token provided.'));
    }
    return from(this.api.call('get', 'auth/current-user', {}, {accessToken})).pipe(
      switchMap((result: any) => {
        this.user = User.fromAPI(result.user);
        return this.getOrganizationsMemberships().pipe(
          map((memberships: OrganizationMembership[]) => {
            this.user!.organizationMemberships = memberships;
            return this.user;  // Return the user with memberships added
          })
        );
      }),
      catchError((error) => {
        console.error('Error retrieving user data', error);
        this.logout().subscribe();  // Log out the user if an error occurs
        return throwError(() => new Error(error.message))
      })  // Properly rethrow the error
    );
  }

  public validateNewEmailToken(token: string): Observable<any> {
    return this.api.call('get', `email/confirm/${token}`);
  }
}
